import React, { useMemo, useState } from "react";
import "./InboxRichieste.css";

// === EMAIL CLASSIFICAZIONE INTELLIGENTE V1 - 2026-08-13 ===
const parseAnalysis = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    return {};
  }
};

const requestModel = (item) => {
  const analysis = parseAnalysis(item?.email_analisi_json);
  const description = String(item?.descrizione || "").trim();
  const request =
    analysis?.request ||
    (description && description !== "Senza descrizione" ? description : "") ||
    item?.email_corpo_originale ||
    item?.lavoriEseguiti ||
    "";

  return {
    analysis,
    request,
    causale: analysis?.causale || item?.causale || "Richiesta intervento",
    proposedClient: analysis?.proposedClient || null,
    proposedAddress:
      analysis?.proposedAddress ||
      analysis?.extractedAddress ||
      analysis?.proposedClient?.indirizzo ||
      "",
    confidence: analysis?.confidence || "legacy",
    senderName:
      item?.email_mittente_nome ||
      analysis?.sender?.name ||
      item?.cliente ||
      "Mittente sconosciuto",
    senderAddress:
      item?.email_mittente_indirizzo ||
      analysis?.sender?.address ||
      "",
    originalSubject:
      item?.email_oggetto_originale ||
      analysis?.originalSubject ||
      item?.causale ||
      "",
    originalBody:
      item?.email_corpo_originale ||
      analysis?.originalBody ||
      item?.lavoriEseguiti ||
      item?.descrizione ||
      "",
  };
};

const ConfidenceBadge = ({ value }) => {
  if (!value || value === "legacy") return <span className="inbox-ai-badge legacy">Da verificare</span>;
  return (
    <span className={`inbox-ai-badge ${value}`}>
      Proposta ${value}
    </span>
  );
};

const InboxRichieste = ({
  richieste = [],
  loading = false,
  onClassifica,
  onElimina,
}) => {
  const rows = Array.isArray(richieste) ? richieste : [];
  const [selectedId, setSelectedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const selected = useMemo(() => {
    if (!rows.length) return null;
    return rows.find((item) => item.id === selectedId) || rows[0];
  }, [rows, selectedId]);

  const model = useMemo(() => requestModel(selected), [selected]);

  const handleEliminaSelected = async () => {
    if (!selected?.id || typeof onElimina !== "function" || deletingId) {
      return;
    }

    const id = selected.id;
    setDeletingId(id);

    try {
      await onElimina(selected);
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) return <p>Caricamento richieste...</p>;

  if (!rows.length) {
    return (
      <div className="inbox-empty">
        <div className="inbox-empty-icon">✉</div>
        <h3>Nessuna richiesta da classificare</h3>
      </div>
    );
  }

  return (
    <div className="inbox-richieste inbox-richieste-v1">
      <aside className="inbox-list">
        <div className="inbox-list-header">
          <div>
            <h2>Richieste</h2>
            <small>Email da interpretare e confermare</small>
          </div>
          <span>{rows.length}</span>
        </div>

        <div className="inbox-items">
          {rows.map((item) => {
            const itemModel = requestModel(item);
            const active = selected?.id === item.id;
            const primary = itemModel.proposedClient?.nome || itemModel.senderName;

            return (
              <button
                type="button"
                className={`inbox-item ${active ? "active" : ""}`}
                onClick={() => setSelectedId(item.id)}
                key={item.id}
              >
                <div className="inbox-item-topline">
                  <strong>{primary || "Mittente sconosciuto"}</strong>
                  <ConfidenceBadge value={itemModel.confidence} />
                </div>
                <span>{itemModel.causale}</span>
                <small>
                  {item.data
                    ? new Date(item.data).toLocaleDateString("it-IT")
                    : "Data non disponibile"}
                </small>
              </button>
            );
          })}
        </div>
      </aside>

      <section className="inbox-detail">
        <div className="inbox-detail-header">
          <div>
            <span className="inbox-foglio">{selected?.numero_foglio_intervento || "—"}</span>
            <h2>{model.causale}</h2>
            <p>
              Mittente: <strong>{model.senderName || "—"}</strong>
              {model.senderAddress ? ` <${model.senderAddress}>` : ""}
            </p>
          </div>

          <div className="inbox-actions">
            <button
              type="button"
              className="inbox-classifica-button"
              disabled={Boolean(deletingId)}
              onClick={() =>
                onClassifica?.({
                  ...selected,
                  _emailClassificationProposal: {
                    cliente: model.proposedClient?.nome || "",
                    clienteId: model.proposedClient?.id || null,
                    indirizzo: model.proposedAddress || "",
                    telefono: model.proposedClient?.telefono || "",
                    email: model.proposedClient?.email || "",
                    causale: model.causale || "",
                    descrizione: model.request || "",
                    lavoriEseguiti: "",
                  },
                })
              }
            >
              Classifica
            </button>

            <button
              type="button"
              className="inbox-elimina-button"
              disabled={Boolean(deletingId)}
              onClick={handleEliminaSelected}
            >
              {deletingId === selected?.id ? "Eliminazione..." : "Elimina"}
            </button>
          </div>
        </div>

        <div className="inbox-proposal-card">
          <div className="inbox-proposal-title">
            <div>
              <strong>Interpretazione proposta</strong>
              <span>Oggetto e corpo email analizzati insieme</span>
            </div>
            <ConfidenceBadge value={model.confidence} />
          </div>

          <div className="inbox-proposal-grid">
            <div>
              <strong>Cliente proposto</strong>
              <span>{model.proposedClient?.nome || "Da verificare"}</span>
            </div>
            <div>
              <strong>Indirizzo proposto</strong>
              <span>{model.proposedAddress || "Da verificare"}</span>
            </div>
            <div className="inbox-proposal-wide">
              <strong>Causale proposta</strong>
              <span>{model.causale}</span>
            </div>
          </div>

          <div className="inbox-request-box">
            <strong>Richiesta / segnalazione</strong>
            <p>{model.request || "Richiesta da leggere nell’email originale."}</p>
          </div>

          <div className="inbox-works-rule">
            <strong>Lavori eseguiti</strong>
            <span>Vuoto fino a quando l’intervento non viene realmente svolto.</span>
          </div>
        </div>

        <details className="inbox-original-email">
          <summary>Email originale</summary>
          <div className="inbox-original-email-content">
            <div>
              <strong>Oggetto originale</strong>
              <p>{model.originalSubject || "—"}</p>
            </div>
            <div>
              <strong>Corpo originale</strong>
              <pre>{model.originalBody || "Nessun testo disponibile"}</pre>
            </div>
          </div>
        </details>
      </section>
    </div>
  );
};

export default InboxRichieste;
